import React, { useState, useEffect } from 'react'
import { message, Pagination, Select, Switch, Modal } from 'antd'
import { getBanners, deleteBanner, updateBanner } from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { AiFillDelete, AiFillEdit, AiOutlineEye } from "react-icons/ai";
import { FaSearch, FaPlus } from 'react-icons/fa';
import { BsImage } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import AddBanner from '../../Banners/AddBanner';

const { Option } = Select;
const { confirm } = Modal;

const AdminBanners = () => {
    const navigate = useNavigate()
    const [banners, setBanners] = useState([])
    const [loading, setLoading] = useState(false)
    const [showEditBanner, setShowEditBanner] = useState(false)
    const [showAddBanner, setShowAddBanner] = useState(false)
    const [selectedBanner, setSelectedBanner] = useState({})
    const [showViewBanner, setShowViewBanner] = useState(false)
    const [action, setAction] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage] = useState(10)
    const [searchQuery, setSearchQuery] = useState("")
    const [typeFilter, setTypeFilter] = useState("")
    const [activeFilter, setActiveFilter] = useState("")

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    // Calculate the current items for the page
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage

    const filteredBanners = banners.filter((banner) => {
        const query = searchQuery.toLowerCase()
        const matchesSearch = (
            banner.title?.toLowerCase().includes(query) ||
            banner.description?.toLowerCase().includes(query) ||
            banner.type?.toLowerCase().includes(query)
        )
        const matchesType = !typeFilter || banner.type === typeFilter
        const matchesActive = activeFilter === "" || banner.isActive === (activeFilter === "true")

        return matchesSearch && matchesType && matchesActive
    })

    const currentBanners = filteredBanners.slice(indexOfFirstItem, indexOfLastItem)

    const fetchBanners = async () => {
        try {
            setLoading(true)
            const response = await getBanners()
            if (response?.data?.success) {
                setBanners(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching banners", error.message)
            if (error.message === 'Network Error' || error.message.includes('ERR_INTERNET_DISCONNECTED')) {
                message.error("Network Error")
                return
            }
            if (error?.response?.status === 401) {
                window.alert("Your account is logged in to another device, please login again!")
                localStorage.removeItem('token')
                window.location.href = "/login"
            }
            if (error?.response?.status === 500) {
                message.error("Server not responding")
            } else {
                message.error("Error fetching banners")
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchBanners()
    }, [action])

    const handleDeleteBanner = async (banner) => {
        confirm({
            title: 'Are you sure you want to delete this banner?',
            content: `Banner: ${banner.title}`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    setLoading(true)
                    const response = await deleteBanner(banner._id)
                    if (response?.data?.success) {
                        message.success(response?.data?.message || "Banner deleted successfully")
                        setAction(!action)
                    }
                } catch (error) {
                    console.log("Error deleting banner", error.message)
                    message.error(error?.response?.data?.message || "Error deleting banner")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const handleToggleActive = async (banner, checked) => {
        try {
            setLoading(true)
            const response = await updateBanner(banner._id, { isActive: checked })
            if (response?.data?.success) {
                message.success(`Banner ${checked ? 'activated' : 'deactivated'} successfully`)
                setAction(!action)
            }
        } catch (error) {
            console.log("Error updating banner status", error.message)
            message.error(error?.response?.data?.message || "Error updating banner status")
        } finally {
            setLoading(false)
        }
    }

    const handleViewBanner = (banner) => {
        setSelectedBanner(banner)
        setShowViewBanner(true)
    }

    const handleEditBanner = (banner) => {
        setSelectedBanner(banner)
        setShowEditBanner(true)
    }

    return (
        <div className="p-6 mt-16">
            {/* Header Section with Add Button */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Banner Management</h2>
                    <p className="text-gray-600 mt-1">Manage promotional banners and campaigns</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowAddBanner(true)}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        <FaPlus className="text-lg" />
                        <span className="font-medium">Add New Banner</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Total Banners</p>
                            <p className="text-2xl font-bold">{banners.length}</p>
                        </div>
                        <div className="text-blue-200 text-2xl">
                            <FaPlus />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Active Banners</p>
                            <p className="text-2xl font-bold">{banners.filter(b => b.isActive).length}</p>
                        </div>
                        <div className="text-green-200 text-2xl">
                            <AiOutlineEye />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Promotional</p>
                            <p className="text-2xl font-bold">{banners.filter(b => b.type === 'promotional').length}</p>
                        </div>
                        <div className="text-purple-200 text-2xl">
                            <BsImage />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">Inactive Banners</p>
                            <p className="text-2xl font-bold">{banners.filter(b => !b.isActive).length}</p>
                        </div>
                        <div className="text-orange-200 text-2xl">
                            <AiFillEdit />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search banners..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            className="w-full"
                            allowClear
                        >
                            <Option value="">All Types</Option>
                            <Option value="promotional">Promotional</Option>
                            <Option value="informational">Informational</Option>
                            <Option value="announcement">Announcement</Option>
                            <Option value="featured">Featured</Option>
                        </Select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <Select
                            value={activeFilter}
                            onChange={setActiveFilter}
                            className="w-full"
                            allowClear
                        >
                            <Option value="">All Status</Option>
                            <Option value="true">Active</Option>
                            <Option value="false">Inactive</Option>
                        </Select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchQuery("")
                                setTypeFilter("")
                                setActiveFilter("")
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 w-full"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Banner Table */}
            <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Banner Image</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentBanners?.length > 0 ? (
                                currentBanners?.map((banner, index) => (
                                    <tr key={banner._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent transition-all duration-200 group">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-600 text-sm font-medium rounded-full group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                                    {(currentPage - 1) * itemsPerPage + index + 1}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="relative">
                                                <img
                                                    src={banner.banner_image}
                                                    alt={banner.altText || banner.title}
                                                    className="h-16 w-24 object-cover rounded-lg border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
                                                    onError={(e) => {
                                                        e.target.src = '/placeholder-image.png'
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-200"></div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="max-w-xs">
                                                <div className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors" title={banner.title}>
                                                    {banner.title}
                                                </div>
                                                <div className="text-sm text-gray-500 truncate mt-1" title={banner.description}>
                                                    {banner.description}
                                                </div>
                                                <div className="text-xs text-gray-400 mt-1">
                                                    Link: {banner.linkUrl || 'No link'}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
                                                banner.type === 'promotional' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                                banner.type === 'informational' ? 'bg-green-100 text-green-800 border border-green-200' :
                                                banner.type === 'announcement' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                                'bg-purple-100 text-purple-800 border border-purple-200'
                                            }`}>
                                                <BsImage className="w-3 h-3 mr-1" />
                                                {banner.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Switch
                                                    checked={banner.isActive}
                                                    onChange={(checked) => handleToggleActive(banner, checked)}
                                                    size="small"
                                                    className="mr-3"
                                                />
                                                <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                                    banner.isActive
                                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                                        : 'bg-red-100 text-red-800 border border-red-200'
                                                }`}>
                                                    {banner.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-1">
                                                <button
                                                    onClick={() => handleViewBanner(banner)}
                                                    className="inline-flex items-center justify-center w-8 h-8 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 rounded-lg transition-all duration-200 group"
                                                    title="View Details"
                                                >
                                                    <AiOutlineEye className="text-sm" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditBanner(banner)}
                                                    className="inline-flex items-center justify-center w-8 h-8 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 rounded-lg transition-all duration-200 group"
                                                    title="Edit Banner"
                                                >
                                                    <AiFillEdit className="text-sm" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteBanner(banner)}
                                                    className="inline-flex items-center justify-center w-8 h-8 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-lg transition-all duration-200 group"
                                                    title="Delete Banner"
                                                >
                                                    <AiFillDelete className="text-sm" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        {loading ? "Loading banners..." : "No banners found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {filteredBanners.length > itemsPerPage && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage}
                        total={filteredBanners.length}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} banners`}
                    />
                </div>
            )}

            {/* Modals */}
            {showViewBanner && (
                <Modal
                    open={showViewBanner}
                    onCancel={() => setShowViewBanner(false)}
                    footer={null}
                    title="Banner Details"
                    width={720}
                >
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <img
                                src={selectedBanner.banner_image}
                                alt={selectedBanner.altText || selectedBanner.title}
                                className="h-32 w-48 object-cover rounded-lg border border-gray-200"
                                onError={(e) => {
                                    e.target.src = '/placeholder-image.png'
                                }}
                            />
                            <div className="flex-1 space-y-2">
                                <div>
                                    <p className="text-sm text-gray-500">Title</p>
                                    <p className="text-base font-semibold text-gray-900">
                                        {selectedBanner.title || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Type</p>
                                    <p className="text-sm text-gray-800 capitalize">
                                        {selectedBanner.type || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Status</p>
                                    <p className="text-sm text-gray-800">
                                        {selectedBanner.isActive ? 'Active' : 'Inactive'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Description</p>
                            <p className="text-sm text-gray-800">
                                {selectedBanner.description || '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Link</p>
                            <p className="text-sm text-blue-600 break-all">
                                {selectedBanner.linkUrl || 'No link'}
                            </p>
                        </div>
                    </div>
                </Modal>
            )}
            {showAddBanner && (
                <AddBanner
                    showModal={showAddBanner}
                    setShowModal={setShowAddBanner}
                    action={action}
                    setAction={setAction}
                />
            )}

            {showEditBanner && (
                <AddBanner
                    showModal={showEditBanner}
                    setShowModal={setShowEditBanner}
                    action={action}
                    setAction={setAction}
                    selectedBanner={selectedBanner}
                    isEdit={true}
                />
            )}

            {loading && <Load />}
        </div>
    )
}

export default AdminBanners
